import { debounce } from "lodash";
import { globalVariables } from "helper/globalVariables";
import Dropdown from "shared/Components/dropdown";
import { useEffect, useRef, useState } from "react";
import { TableWithPagination } from "shared/Components/tableWithPagination";
import { globalClasses } from "helper/globalClasses";
import { JudgeListMobileTable } from "../ViewJudges/components/JudgeListMobileTable";
import { useLazyQuery } from "@apollo/client";
import { getAllHost, approveUpdateRole, rejecteUpdateRole } from "redux/features/hostATrial/hostATrial";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { useMediaQuery } from "react-responsive";
import { client } from "helper/apolloConfig";
import { Button } from "shared/Components/button";
import SearchBar from "shared/Components/search";
import { SearchLocation } from "shared/Components/searchLocation";
import { MultiSelectChipsWithSearchDropdown } from "shared/Components/multiSelectWithSearchAndChips";
import PageInfo from "shared/Components/pageInfo";

const headData = [
  globalVariables.name,
  globalVariables.email,
  globalVariables.phoneNumber,
  globalVariables.state,
  globalVariables.city,
  globalVariables.status,
];

const noviceDropdownData = [
  globalVariables.sit,
  globalVariables.novice,
  globalVariables.advanced,
  globalVariables.excellent,
  globalVariables.premier,
];

export const UpdateHosts = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { paginatedDataCls } = globalClasses;

  const [page, setPage] = useState(1);
  
  const [getData, { loading, data, refetch }] = useLazyQuery(getAllHost)

  const [levelFilter, setLevelFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  const [search, setSearch] = useState("");

  const hostData = data?.getAllHost.data || [];

  const popUpModalRef = useRef();

  const onSearch = (event) => {
    const value = event?.target?.value;
    setSearch(value);
  };

  const debouncedSearch = debounce(onSearch, 1000);

  const handleFilterChange = (selectedOptions) => {
    const selectedValues = selectedOptions.map((option) => option.label);
    setLevelFilter(selectedValues);
  };
  
  const options = noviceDropdownData.map((data, index) => ({
    label: data,
    value: `option_${index}`,
  }));
  
  const [apiData, setApiData] = useState({
    title: "",
    desc: "",
    type: "pending",
  });

  const handlePaginationData = (page = 1) => {
    setPage(page);
  };

  useEffect(() => {
    getData({
      variables: {
        pageNumber: page,
        pageSize: 10,
        level: levelFilter.length === 0 ? null : levelFilter,
        city: cityFilter,
        state: stateFilter,
        search,
        status: "active",
      },
    });
  }, [page, levelFilter, cityFilter, stateFilter, search ]);

  const onChangeStatus = async (id, option) => {
    const mutation = option == "Approved" ? approveUpdateRole : rejecteUpdateRole;
    try {
      const result = await client.mutate({
        mutation, 
        variables: {
          authId: id,
        },
      });
      const { approveUpdateRole, rejecteUpdateRole } = result.data;

      if (approveUpdateRole || rejecteUpdateRole) {
        setApiData({
          title: "Success",
          desc: approveUpdateRole?.message || rejecteUpdateRole?.message,
          type: "success",
        });
        
        refetch();
        popUpModalRef.current?.openModal();
      } else {
        throw new Error("Unexpected response from server.");
      }
    } catch (error) {
      setApiData({
        title: "Error",
        desc: error.message || "An error occurred.",
        type: "error",
      });
    }
  };
  
  const renderButton = (id, status) => {
    return (
      <>
        <div className="flex justify-center">
          <Dropdown
            defaultOption={"Active"}
            width={"w-[150px]"}
            mainContent="w-100%"
            dropdownOptions={["Approved", "Rejected"]}
            onChange={(option) => {
              if (option) onChangeStatus(id, option);
            }}
          />
        </div>
      </>
    );
  };

  const renderTableBody = () => {
    return hostData?.map((host, index) => (
      <tr key={host.name}>
        <td className={paginatedDataCls(index)}>{host.name ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{host.email ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{host.phone_number ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{host.state ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{host.city ?? "-"}</td>
        <td className={paginatedDataCls(index)}>{renderButton(host.authId, host.hostStatus)}</td>
      </tr>
    ));
  };

  const onClearFilters = () => {
    setCityFilter(null);
    setStateFilter(null);
    setLevelFilter([]);
    setSearch("");
  };

  return (
    <>
      <PageInfo title={globalVariables.viewHosts}>
        <Button
          width={"w-auto"}
          label="Clear All Filters"
          onClick={onClearFilters} 
        />
      </PageInfo>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 my-5 order-3">
        <SearchBar
          defaultValue={search}
          isShadow={true}
          width={"w-full"}
          onChange={debouncedSearch}
          placeholder={"Name"}
          isFaded={true}
        />
        <SearchLocation
          isShadow={true}
          width={"w-full"}
          name="state"
          placeholder={globalVariables.state}
          types={["(states)"]}
          onSelectLocation={(location) => {
            setStateFilter(location);
          }}
          defaultValue={stateFilter}
        />
        <SearchLocation
          isShadow={true}
          width={"w-full"}
          name="city"
          placeholder={globalVariables.city}
          types={["(cities)"]}
          onSelectLocation={(location) => {
            setCityFilter(location);
          }}
          searchBy={stateFilter}
          defaultValue={cityFilter}
        />
        <MultiSelectChipsWithSearchDropdown
          width={"w-full"}
          placeholder={globalVariables.level}
          options={options}
          label=""
          onChange={handleFilterChange}
          defaultSelectedOptions={levelFilter}
        />
      </div>
      <div className="order-4">
        {isMobile ? (
          <JudgeListMobileTable
            data={hostData}
            loading={loading}
          />
        ) : (
          <TableWithPagination
            headData={headData}
            tableBody={renderTableBody()}
            itemsPerPage={10}
            tableHeight={"md:h-[calc(100vh-10rem)] lg:h-[100vh]"}
            loading={loading}
            totalPage={hostData?.totalPages}
            handlePageChange={(page) => handlePaginationData(page)}
          />
        )}        
      </div>
      <PopUpMessages
        title={apiData.title}
        desc={apiData.desc}
        type={apiData.type}
        ref={popUpModalRef}
      />
    </>
  );
};
